import type { ThemeOptions } from '@mui/material/styles'
import { ThemeProvider, alpha, createTheme } from '@mui/material/styles'
import * as React from 'react'
import { inputsCustomizations } from './customizations/inputs'
import { navigationCustomizations } from './customizations/navigation'
import { surfacesCustomizations } from './customizations/surfaces'
import { shadows, shape, typography } from './themePrimitives'

interface AppThemeProps {
  children: React.ReactNode
  disableCustomTheme?: boolean
  themeComponents?: ThemeOptions['components']
}

// Custom Themeing
// Augment the palette to include an tertiary color
declare module '@mui/material/styles' {
  interface Palette {
    tertiary: Palette['primary']
  }

  interface PaletteOptions {
    tertiary?: PaletteOptions['primary']
  }
}

// Update the Button's color options to include an tertiary option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsColorOverrides {
    tertiary: true
  }
}

export default function AppTheme({
  children,
  disableCustomTheme,
  themeComponents,
}: AppThemeProps) {
  function lightenColor(color: string, percent: number) {
    const num = parseInt(color.replace('#', ''), 16)
    const amt = Math.round(2.55 * percent)
    const R = (num >> 16) + amt
    const G = ((num >> 8) & 0x00ff) + amt
    const B = (num & 0x0000ff) + amt

    return (
      '#' +
      (
        0x1000000 +
        (R < 255 ? (R < 1 ? 0 : R) : 255) * 0x10000 +
        (G < 255 ? (G < 1 ? 0 : G) : 255) * 0x100 +
        (B < 255 ? (B < 1 ? 0 : B) : 255)
      )
        .toString(16)
        .slice(1)
    )
  }

  function darkenColor(color: string, percent: number) {
    return lightenColor(color, -percent)
  }

  // Ant Design 5.0 color palette - modern, professional, enterprise-ready
  const primaryBaseColor = '#1677FF' // Ant Design Blue - primary brand color
  const secondaryBaseColor = '#13C2C2' // Cyan - complementary color
  const tertiaryBaseColor = '#8C8C8C' // Neutral gray for tertiary actions
  const infoBaseColor = '#1677FF' // Blue for info (same as primary in Ant Design)
  const successBaseColor = '#52C41A' // Green - success states
  const errorBaseColor = '#FF4D4F' // Red - error states
  const warningBaseColor = '#FAAD14' // Gold/Orange - warnings

  const theme = React.useMemo(() => {
    return disableCustomTheme
      ? {}
      : createTheme({
          // For more details about CSS variables configuration, see https://mui.com/material-ui/customization/css-theme-variables/configuration/
          cssVariables: {
            colorSchemeSelector: 'data-mui-color-scheme',
            cssVarPrefix: 'cp',
          },
          // colorSchemes, // Recently added in v6 for building light & dark mode app, see https://mui.com/material-ui/customization/palette/#color-schemes
          colorSchemes: {
            light: {
              palette: {
                background: {
                  default: '#F5F5F5', // Ant Design light gray background
                  paper: '#FFFFFF', // Pure white for components
                },
                text: {
                  primary: 'rgba(0, 0, 0, 0.88)', // Ant Design primary text
                  secondary: 'rgba(0, 0, 0, 0.65)', // Ant Design secondary text
                  disabled: 'rgba(0, 0, 0, 0.5)', // Ant Design disabled text
                },
                action: {
                  hover: 'rgba(0, 0, 0, 0.06)', // Ant Design hover state
                  selected: alpha(primaryBaseColor, 0.1), // 10% primary color
                  hoverOpacity: 0.06,
                  selectedOpacity: 0.1,
                  disabledOpacity: 0.25,
                  focus: alpha(primaryBaseColor, 0.12),
                },
                primary: {
                  main: primaryBaseColor,
                  light: lightenColor(primaryBaseColor, 20),
                  dark: darkenColor(primaryBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                tertiary: {
                  main: tertiaryBaseColor,
                  light: lightenColor(tertiaryBaseColor, 20),
                  dark: darkenColor(tertiaryBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                secondary: {
                  main: secondaryBaseColor,
                  light: lightenColor(secondaryBaseColor, 20),
                  dark: darkenColor(secondaryBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                info: {
                  main: infoBaseColor,
                  light: lightenColor(infoBaseColor, 20),
                  dark: darkenColor(infoBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                error: {
                  main: errorBaseColor,
                  light: lightenColor(errorBaseColor, 20),
                  dark: darkenColor(errorBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                warning: {
                  main: warningBaseColor,
                  light: lightenColor(warningBaseColor, 20),
                  dark: darkenColor(warningBaseColor, 15),
                  contrastText: 'rgba(0, 0, 0, 0.88)', // Dark text for warning
                },
                success: {
                  main: successBaseColor,
                  light: lightenColor(successBaseColor, 20),
                  dark: darkenColor(successBaseColor, 15),
                  contrastText: '#FFFFFF',
                },
                divider: 'rgba(5, 5, 5, 0.06)', // Ant Design divider color
                grey: {
                  50: '#FAFAFA',
                  100: '#F5F5F5',
                  200: '#E8E8E8',
                  300: '#D9D9D9',
                  400: '#BFBFBF',
                  500: '#8C8C8C',
                  600: '#595959',
                  700: '#434343',
                  800: '#262626',
                  900: '#1F1F1F',
                },
                // ...Custom tokens
              },
            },
            dark: {
              palette: {
                background: { default: '#1E1D25', paper: '#2F2E39' },
                divider: 'rgba(255, 255, 255, 0.12)',
                // text
                text: {
                  primary: '#fff',
                  secondary: 'rgba(255, 255, 255, 0.7)',
                  disabled: 'rgba(255, 255, 255, 0.5)',
                },
                // Buttons
                action: {
                  active: '#fff',
                  hover: 'rgba(255, 255, 255, 0.08)',
                  selected: 'rgba(255, 255, 255, 0.16)',
                  disabled: 'rgba(255, 255, 255, 0.3)',
                  disabledBackground: 'rgba(255, 255, 255, 0.12)',
                },
                primary: {
                  main: '#25CC8E',
                  light: '#2BC8BF',
                },
                secondary: {
                  main: '#9FA8DA',
                },
                info: {
                  main: '#2BC8BF',
                },
                error: {
                  main: '#CF6679',
                },
                warning: {
                  main: '#F90',
                },
                // ...Custom tokens
                tertiary: {
                  main: '#504E64',
                  light: lightenColor(tertiaryBaseColor, 30),
                  dark: '#3d3c4a',
                  contrastText: '#1E1D25',
                },
              },
            },
          },
          typography,
          shadows,
          shape,
          components: {
            // Override the components here
            ...inputsCustomizations,
            ...navigationCustomizations,
            ...surfacesCustomizations,
            ...themeComponents,
          },
        })
  }, [disableCustomTheme, themeComponents])

  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>
  }

  return (
    <ThemeProvider theme={theme} disableTransitionOnChange>
      {children}
    </ThemeProvider>
  )
}
